// Fichier : ProfileForm.tsx
// Ce fichier contient la définition et l'implémentation du formulaire de connexion d'un utilisateur.

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useDispatch, useSelector } from "react-redux";
import { checkAuth } from "../../state/authSlice";
import { RootState, AppDispatch } from "../../state/store";
import { useLoginUserMutation } from "../../state/authSlice";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { formSchema } from "@/zod Schemas/zodLoginSchema";

export function Login() {
  const dispatch = useDispatch<AppDispatch>(); // Hook pour dispatcher des actions Redux
  const auth = useSelector((state: RootState) => state.auth); // Hook pour sélectionner l'état de l'authentification dans le store Redux
  const [loginUser, { isLoading }] = useLoginUserMutation(); // Hook pour déclencher la mutation de connexion utilisateur
  const navigate = useNavigate(); // Hook pour la navigation

  // Initialiser le formulaire avec react-hook-form et zodResolver pour la validation
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  // Fonction pour gérer la soumission du formulaire
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await loginUser(values).unwrap(); // Tentative de connexion utilisateur
      dispatch(checkAuth()); // Vérification de l'authentification après connexion
      navigate("/users/all"); // Navigation vers la page des utilisateurs en cas de succès
    } catch (err) {
      console.error("Login failed:", err); // Gestion des erreurs de connexion
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-[70vh] h-[60vh] flex items-center space-y-8">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="bg-white shadow-md rounded w-full flex flex-col gap-8 px-8 pt-6 pb-8 mb-4"
          >
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      placeholder="Username"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    This is your public display name.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      type="password"
                      placeholder="Password"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Your password must be at least 5 characters long.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              {isLoading ? "Logging in..." : "Login"}
            </Button>
            {auth.status === "loading" && <p>Loading...</p>}
            {auth.status === "failed" && (
              <p className="text-red-500">{auth.error}</p>
            )}
          </form>
        </Form>
      </div>
    </div>
  );
}

export default Login;
